import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authApi } from "@/lib/saathiClient";

async function fetchStats() {
  try {
    return (await authApi.get("/org/stats")).data;
  } catch (e) {
    if (e?.response?.status !== 401) throw e;
    await authApi.post("/auth/refresh");
    return (await authApi.get("/org/stats")).data;
  }
}

const Stat = ({ label, value, testid }) => (
  <div className="bg-white border border-[#EDE0D4] rounded-2xl p-5">
    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#8B7355] mb-2">{label}</div>
    <div className="font-heading text-[40px] leading-none text-[#1A1A2E]" data-testid={testid}>{value}</div>
  </div>
);

function LinkCard({ slug }) {
  const link = `${window.location.origin}/o/${slug}`;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* silent */ }
  };
  return (
    <div className="bg-[#1A1A2E] text-[#FFFBF5] rounded-3xl p-6 sm:p-8 mb-6" data-testid="org-link-card">
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E07B39] mb-2">your saathi link</div>
      <p className="text-[14px] text-[#FFFBF5]/70 mb-4">Put this on kiosks, waiting-room screens, posters or your website. Chats through this link are unlimited and counted here.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <code className="flex-1 bg-white/10 rounded-full px-5 py-3 text-[13px] break-all" data-testid="org-link-url">{link}</code>
        <button onClick={copy} className="bg-[#E07B39] hover:bg-[#c96a2e] rounded-full px-6 py-3 text-[14px] font-medium transition-colors" data-testid="org-link-copy-btn">{copied ? "Copied" : "Copy"}</button>
        <a href={`/o/${slug}`} target="_blank" rel="noreferrer" className="border border-white/25 hover:border-[#E07B39] rounded-full px-6 py-3 text-[14px] text-center transition-colors" data-testid="org-link-open-btn">Open</a>
      </div>
    </div>
  );
}

function DailyChart({ daily }) {
  const max = Math.max(1, ...daily.map((d) => d.conversations));
  return (
    <div className="bg-white border border-[#EDE0D4] rounded-2xl p-5" data-testid="org-daily-chart">
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#8B7355] mb-4">conversations · last 30 days</div>
      <div className="flex items-end gap-[3px] h-32">
        {daily.map((d) => (
          <div key={d.date} title={`${d.date}: ${d.conversations}`} className="flex-1 bg-[#E07B39]/80 hover:bg-[#E07B39] rounded-t transition-colors" style={{ height: `${Math.max(2, (d.conversations / max) * 100)}%` }} />
        ))}
      </div>
    </div>
  );
}

function Languages({ byLang }) {
  const rows = Object.entries(byLang).sort((a, b) => b[1] - a[1]);
  return (
    <div className="bg-white border border-[#EDE0D4] rounded-2xl p-5" data-testid="org-languages">
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#8B7355] mb-4">languages used</div>
      {rows.length === 0 && <div className="text-[13px] text-[#8B7355]">No conversations yet. Share your link to get started.</div>}
      {rows.map(([l, n]) => (
        <div key={l} className="flex justify-between py-1.5 text-[14px] border-b border-[#EDE0D4]/60 last:border-0">
          <span className="text-[#1A1A2E]">{l}</span><span className="font-mono text-[#E07B39]">{n}</span>
        </div>
      ))}
    </div>
  );
}

export default function OrgDashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { setData(await fetchStats()); } catch { nav("/org/login"); }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const logout = async () => {
    try { await authApi.post("/auth/logout"); } catch { /* silent */ }
    nav("/org/login");
  };

  if (!data) return <div className="saathi-page flex items-center justify-center min-h-screen font-mono text-[12px] text-[#8B7355]" data-testid="org-dashboard-loading">loading…</div>;

  return (
    <div className="saathi-page" data-testid="org-dashboard">
      <div className="saathi-texture" />
      <div className="max-w-4xl mx-auto px-6 sm:px-10 py-10 relative z-10">
        <div className="flex items-start justify-between mb-10">
          <div>
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#8B7355] mb-2">saathi partner</div>
            <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A2E] leading-none" data-testid="org-dashboard-name">{data.name}</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="bg-white border border-[#EDE0D4] hover:border-[#E07B39] rounded-full px-4 py-2 text-[13px] transition-colors" data-testid="org-refresh-btn">Refresh</button>
            <button onClick={logout} className="bg-white border border-[#EDE0D4] hover:border-[#E07B39] rounded-full px-4 py-2 text-[13px] transition-colors" data-testid="org-logout-btn">Sign out</button>
          </div>
        </div>
        <LinkCard slug={data.slug} />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <Stat label="people helped" value={data.conversations} testid="org-stat-conversations" />
          <Stat label="questions asked" value={data.questions} testid="org-stat-questions" />
          <Stat label="documents explained" value={data.documents} testid="org-stat-documents" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2"><DailyChart daily={data.daily} /></div>
          <Languages byLang={data.by_language} />
        </div>
      </div>
    </div>
  );
}
